// =============================================================================
// TaskListSelector.tsx — Displays existing task lists for a deal and allows
// the user to create a new inspection or select an existing one.
// =============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { API } from 'aws-amplify';
import gql from 'graphql-tag';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface TaskListSelectorProps {
  dealId: string;
  onTaskListSelected: (taskListId: string) => void;
  onBack?: () => void;
}

interface TaskListSummary {
  id: string;
  templateName: string;
  taskListName: string | null;
  inspectionType: string | null;
  isCompleted: boolean;
  createdAt: string | null;
  completedAt: string | null;
}

interface GetTaskListItemsResponse {
  success: boolean;
  data: {
    items: unknown[];
    taskLists: TaskListSummary[];
  };
}

// -----------------------------------------------------------------------------
// GraphQL Query
// -----------------------------------------------------------------------------

const GET_TASK_LIST_ITEMS = gql`
  query GetTaskListItems(
    $dealID: String!
    $autoCreate: Boolean
    $taskTypes: [String]
  ) {
    getTaskListItems(
      dealID: $dealID
      autoCreate: $autoCreate
      taskTypes: $taskTypes
    )
  }
`;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const TaskListSelector: React.FC<TaskListSelectorProps> = ({
  dealId,
  onTaskListSelected,
  onBack,
}) => {
  const { theme } = useTheme();

  const [taskLists, setTaskLists] = useState<TaskListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch existing task lists
  // ---------------------------------------------------------------------------

  const fetchTaskLists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = (await API.graphql({
        query: GET_TASK_LIST_ITEMS as any,
        variables: { dealID: dealId, autoCreate: false },
      })) as GraphQLResult<{ getTaskListItems: string }>;

      if (result.data?.getTaskListItems) {
        const parsed: GetTaskListItemsResponse = JSON.parse(
          result.data.getTaskListItems
        );
        if (parsed.success && parsed.data?.taskLists) {
          setTaskLists(parsed.data.taskLists);
        } else {
          setTaskLists([]);
        }
      } else {
        setTaskLists([]);
      }
    } catch (err: any) {
      console.error('[TaskListSelector] Failed to fetch task lists:', err);
      const message =
        err?.errors?.[0]?.message ||
        err?.message ||
        'Failed to load inspections. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchTaskLists();
  }, [fetchTaskLists]);

  // ---------------------------------------------------------------------------
  // Create new inspection
  // ---------------------------------------------------------------------------

  const handleStartNewInspection = useCallback(async () => {
    setCreating(true);
    setError(null);

    try {
      const result = (await API.graphql({
        query: GET_TASK_LIST_ITEMS as any,
        variables: {
          dealID: dealId,
          autoCreate: true,
          taskTypes: ['question', 'photo'],
        },
      })) as GraphQLResult<{ getTaskListItems: string }>;

      console.log(
        '[TaskListSelector] autoCreate response:',
        result.data?.getTaskListItems
      );

      if (result.data?.getTaskListItems) {
        const parsed: GetTaskListItemsResponse = JSON.parse(
          result.data.getTaskListItems
        );
        console.log(
          '[TaskListSelector] parsed taskLists:',
          parsed.data?.taskLists?.map((tl) => ({
            id: tl.id,
            name: tl.templateName,
            createdAt: tl.createdAt,
          }))
        );
        if (parsed.success && parsed.data?.taskLists?.length > 0) {
          const newList = parsed.data.taskLists[0];
          onTaskListSelected(newList.id);
          return;
        }
      }

      setError('Failed to create a new inspection. Please try again.');
    } catch (err: any) {
      console.error('[TaskListSelector] Failed to create task list:', err);
      const message =
        err?.errors?.[0]?.message ||
        err?.message ||
        'Failed to create inspection. Please try again.';
      setError(message);
    } finally {
      setCreating(false);
    }
  }, [dealId, onTaskListSelected]);

  // ---------------------------------------------------------------------------
  // Render: Loading state
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 300,
          backgroundColor: theme.background1,
        }}
      >
        <CircularProgress size={32} sx={{ color: theme.primary }} />
        <Typography variant="body2" sx={{ color: theme.secondary, mt: 2 }}>
          Loading inspections...
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Error state
  // ---------------------------------------------------------------------------

  if (error && taskLists.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 300,
          px: 3,
          backgroundColor: theme.background1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: theme.text, textAlign: 'center', mb: 2 }}
        >
          {error}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchTaskLists}
          sx={{
            textTransform: 'none',
            backgroundColor: theme.primary,
            color: theme.textCTA,
            borderRadius: '8px',
            '&:hover': { backgroundColor: theme.primaryDark },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Main UI
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.background1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.stroke}`,
        }}
      >
        {onBack && (
          <Button
            size="small"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: 'none',
              color: theme.text,
              minWidth: 'auto',
              mr: 1,
            }}
          >
            Back
          </Button>
        )}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: theme.text, flex: 1 }}
        >
          Inspections
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {/* Error banner (non-blocking) */}
        {error && (
          <Box
            sx={{
              backgroundColor: theme.fill1,
              borderRadius: '8px',
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.text }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Empty state */}
        {taskLists.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.text, textAlign: 'center', mb: 1 }}
            >
              No inspections yet
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.secondary, textAlign: 'center', mb: 3 }}
            >
              Start a new AI-assisted inspection for this deal.
            </Typography>
            <Button
              variant="contained"
              startIcon={creating ? undefined : <AddIcon />}
              onClick={handleStartNewInspection}
              disabled={creating}
              sx={{
                textTransform: 'none',
                backgroundColor: theme.primary,
                color: theme.textCTA,
                borderRadius: '8px',
                px: 3,
                '&:hover': { backgroundColor: theme.primaryDark },
                '&:disabled': {
                  backgroundColor: theme.primaryLight,
                  color: theme.textCTA,
                },
              }}
            >
              {creating ? (
                <CircularProgress size={20} sx={{ color: theme.textCTA }} />
              ) : (
                'Start New Inspection'
              )}
            </Button>
          </Box>
        ) : (
          <>
            {/* Task list cards */}
            {taskLists.map((taskList) => (
              <Box
                key={taskList.id}
                onClick={() => onTaskListSelected(taskList.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  mb: 1.5,
                  borderRadius: '8px',
                  border: `1px solid ${theme.stroke}`,
                  backgroundColor: theme.background2,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: theme.fill1,
                  },
                }}
                role="button"
                tabIndex={0}
                aria-label={`Select inspection: ${
                  taskList.templateName || taskList.taskListName || 'Untitled'
                }`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onTaskListSelected(taskList.id);
                  }
                }}
              >
                {/* Completion icon */}
                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                  {taskList.isCompleted ? (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ color: theme.secondary, fontSize: 24 }}
                    />
                  )}
                </Box>

                {/* Card content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {taskList.templateName ||
                      taskList.taskListName ||
                      'Untitled Inspection'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.secondary }}>
                    {formatDate(taskList.createdAt)}
                    {taskList.isCompleted && taskList.completedAt
                      ? ` · Completed ${formatDate(taskList.completedAt)}`
                      : ''}
                  </Typography>
                </Box>

                {/* Status badge */}
                <Box
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: taskList.isCompleted
                      ? 'rgba(76, 175, 80, 0.1)'
                      : 'rgba(255, 152, 0, 0.1)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: taskList.isCompleted ? '#4caf50' : '#ff9800',
                      fontSize: 11,
                    }}
                  >
                    {taskList.isCompleted ? 'Complete' : 'In Progress'}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Start New Inspection button */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={creating ? undefined : <AddIcon />}
                onClick={handleStartNewInspection}
                disabled={creating}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: theme.primary,
                  color: theme.primary,
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: theme.primaryDark,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                  '&:disabled': {
                    borderColor: theme.primaryLight,
                    color: theme.primaryLight,
                  },
                }}
              >
                {creating ? (
                  <CircularProgress size={20} sx={{ color: theme.primary }} />
                ) : (
                  'Start New Inspection'
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TaskListSelector;
